// localStorage-backed auth + game-session helpers.
//
// Auth record shape (Phase 1.5+):
//   { pubkey, signerType: 'nip46' | 'nip07' | 'nsec' | 'anon', authedAt, sessionToken? }
// Legacy stub records without signerType are still readable; they get
// treated as anonymous.
//
// For signerType === 'nsec' the nsec1 is persisted under the SEPARATE key
// `gives.nsec` — now ENCRYPTED AT REST as a SecretBox (never plaintext).
// Default key source is a non-extractable device key in IndexedDB
// (transparent, no passphrase); an optional passphrase upgrades it. The
// plaintext nsec lives ONLY in the in-memory `cachedNsec`, populated once
// at boot by `initNsecUnlock()`. `loadNsec()` stays synchronous (returns
// the cache) so the 16 game signer-wiring call sites are untouched. Never
// read `gives.nsec` outside the signer wiring; never send it to the Worker.

import {
  generateDeviceKey,
  sealWithDeviceKey,
  openWithDeviceKey,
  openWithPassphrase,
  sealWithPassphrase,
  isSecretBox,
  type SecretBox,
} from '@nostr-gives/nostr-auth';

const AUTH_KEY = 'gives.session';
const NSEC_KEY = 'gives.nsec';

// In-memory plaintext nsec. Never persisted here; localStorage holds only
// the SecretBox ciphertext.
let cachedNsec: string | null = null;

export type SessionSignerType = 'nip46' | 'nip07' | 'nsec' | 'anon';

export interface StubAuth {
  pubkey: string;
  authedAt: number;
  signerType?: SessionSignerType;
  sessionToken?: string;
}

export function loadAuth(): StubAuth | null {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StubAuth>;
    if (typeof parsed.pubkey === 'string' && typeof parsed.authedAt === 'number') {
      const out: StubAuth = { pubkey: parsed.pubkey, authedAt: parsed.authedAt };
      if (
        parsed.signerType === 'nip46' ||
        parsed.signerType === 'nip07' ||
        parsed.signerType === 'nsec' ||
        parsed.signerType === 'anon'
      ) {
        out.signerType = parsed.signerType;
      }
      if (typeof parsed.sessionToken === 'string') {
        out.sessionToken = parsed.sessionToken;
      }
      return out;
    }
  } catch {
    // ignore
  }
  return null;
}

export function saveAuth(auth: StubAuth): void {
  try {
    localStorage.setItem(AUTH_KEY, JSON.stringify(auth));
  } catch {
    // ignore
  }
}

export function clearAuth(): void {
  cachedNsec = null;
  try {
    localStorage.removeItem(AUTH_KEY);
    // Always clear nsec alongside; signing out should never leave a
    // dangling nsec in storage even if the session record was already gone.
    localStorage.removeItem(NSEC_KEY);
  } catch {
    // ignore
  }
}

// ── nsec device key (IndexedDB, non-extractable) ────────────────────────────
const IDB_NAME = 'gives.keystore';
const IDB_STORE = 'keys';
const DEVICE_KEY_ID = 'nsec-device-key';

function openIdb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(IDB_STORE)) req.result.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbReq<T>(store: IDBObjectStore, op: 'get' | 'put', key: string, val?: unknown): Promise<T> {
  return new Promise((resolve, reject) => {
    const r = op === 'get' ? store.get(key) : store.put(val, key);
    r.onsuccess = () => resolve(r.result as T);
    r.onerror = () => reject(r.error);
  });
}

/** Load the device AES-GCM key from IndexedDB, generating + persisting on first use. */
async function getDeviceKey(): Promise<CryptoKey> {
  const db = await openIdb();
  const existing = await idbReq<CryptoKey | undefined>(
    db.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE), 'get', DEVICE_KEY_ID,
  );
  if (existing) return existing;
  const key = await generateDeviceKey();
  await idbReq(db.transaction(IDB_STORE, 'readwrite').objectStore(IDB_STORE), 'put', DEVICE_KEY_ID, key);
  return key;
}

/**
 * Persist the user's nsec1, ENCRYPTED at rest. Device-key mode by default
 * (no passphrase); pass `passphrase` to seal under PBKDF2 instead. Sets the
 * in-memory cache synchronously so `loadNsec()` works immediately, even
 * before the async seal resolves. ONLY call from the login modal.
 */
export async function saveNsec(nsec: string, passphrase?: string): Promise<void> {
  cachedNsec = nsec; // available to loadNsec() this session immediately
  try {
    const box: SecretBox = passphrase
      ? await sealWithPassphrase(nsec, passphrase)
      : await sealWithDeviceKey(nsec, await getDeviceKey());
    localStorage.setItem(NSEC_KEY, JSON.stringify(box));
  } catch {
    // ignore (cache still holds the nsec for this session)
  }
}

/**
 * Decrypt the stored nsec into memory at boot. Device-key boxes unlock
 * transparently; passphrase-mode boxes stay locked until
 * `unlockNsecWithPassphrase()` is called. Also migrates any legacy plaintext
 * `gives.nsec` to a sealed device-key box on first run. Call once at startup
 * (main.ts) before games can publish. Idempotent.
 */
export async function initNsecUnlock(): Promise<void> {
  try {
    const raw = localStorage.getItem(NSEC_KEY);
    if (!raw) return;
    // Legacy plaintext migration: old installs stored the bare nsec1 string.
    if (raw.startsWith('nsec1')) {
      cachedNsec = raw;
      await saveNsec(raw); // re-seal with the device key
      return;
    }
    let box: unknown;
    try { box = JSON.parse(raw); } catch { return; }
    if (!isSecretBox(box)) return;
    if (box.mode === 'device') {
      cachedNsec = await openWithDeviceKey(box, await getDeviceKey());
    }
    // passphrase-mode: leave locked; UI must call unlockNsecWithPassphrase().
  } catch {
    cachedNsec = null;
  }
}

/** True if a passphrase-mode nsec box is stored (UI should prompt to unlock). */
export function isNsecPassphraseLocked(): boolean {
  try {
    const raw = localStorage.getItem(NSEC_KEY);
    if (!raw || raw.startsWith('nsec1')) return false;
    const box = JSON.parse(raw) as unknown;
    return isSecretBox(box) && box.mode === 'passphrase' && cachedNsec === null;
  } catch {
    return false;
  }
}

/** Unlock a passphrase-mode nsec box into memory. Returns false on wrong passphrase. */
export async function unlockNsecWithPassphrase(passphrase: string): Promise<boolean> {
  try {
    const raw = localStorage.getItem(NSEC_KEY);
    if (!raw) return false;
    const box = JSON.parse(raw) as unknown;
    if (!isSecretBox(box) || box.mode !== 'passphrase') return false;
    cachedNsec = await openWithPassphrase(box, passphrase);
    return true;
  } catch {
    return false;
  }
}

/**
 * Return the in-memory nsec (populated by `initNsecUnlock` at boot or by
 * `saveNsec` at login). Synchronous by design: the signer-wiring call sites
 * in every game read it directly. Returns null if not unlocked.
 */
export function loadNsec(): string | null {
  return cachedNsec;
}

export function gameSessionKey(gameId: string): string {
  return `gives.session.${gameId}`;
}

export function loadGameSession(gameId: string): string | null {
  try {
    return localStorage.getItem(gameSessionKey(gameId));
  } catch {
    return null;
  }
}

export function saveGameSession(gameId: string, token: string): void {
  try {
    localStorage.setItem(gameSessionKey(gameId), token);
  } catch {
    // ignore
  }
}

export function clearGameSession(gameId: string): void {
  try {
    localStorage.removeItem(gameSessionKey(gameId));
  } catch {
    // ignore
  }
}

/**
 * True if `token` is missing, malformed, or its JWT `iat` claim is older
 * than `maxAgeSeconds`. Use this on game mount before rehydrating a
 * sessionToken from localStorage: if stale, drop the stored token and
 * call /start fresh instead.
 *
 * Why this exists (PM 2026-06-16):
 *   Worker routes with a wall-clock cap (word-ladder, letter-boxed,
 *   acrostic, cross-number) measure elapsed time against the JWT's `sa`
 *   claim. A stored token from a session minted 85+ minutes ago will
 *   trip the cap on the first /submit and return `timed-out` before the
 *   player has typed anything. Defensive guard: any game that rehydrates
 *   a sessionToken from localStorage should call `isSessionStale(token)`
 *   first; if true, drop the token and start a fresh round.
 *
 * Default threshold: 600s (10 minutes). Active gameplay refreshes the
 * token on every /submit response so an actively-playing user never
 * trips this; only abandoned tabs do.
 *
 * Note: signature verification is the worker's job. This helper only
 * decodes the unauthenticated payload to read the `iat` timestamp. A
 * forged token with a fresh `iat` is still rejected by the worker on
 * /submit; this helper just prevents the round-trip in the
 * unambiguously-stale case.
 */
export function isSessionStale(
  token: string | null | undefined,
  maxAgeSeconds = 600,
): boolean {
  if (!token) return true;
  const parts = token.split('.');
  if (parts.length < 2) return true;
  const payloadRaw = parts[1] ?? '';
  if (!payloadRaw) return true;
  try {
    // JWT payloads are base64url. atob handles base64; convert first.
    const b64 = payloadRaw.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const decoded = atob(padded);
    const payload = JSON.parse(decoded) as { iat?: unknown };
    if (typeof payload.iat !== 'number' || !Number.isFinite(payload.iat)) {
      return true;
    }
    const nowSeconds = Math.floor(Date.now() / 1000);
    const ageSeconds = nowSeconds - payload.iat;
    return ageSeconds > maxAgeSeconds;
  } catch {
    return true;
  }
}
